import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import authAPI from "../../../services/authApi";
import ClientProfileSinglePage from "./ClientProfileSinglePage";
import { usePatientsBase } from "../../../utils/hubPaths";
import { EMPTY_EDIT, patientToEdit } from "./patientConstants";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { HubScreen } from "../AdminPortal/HubPwa";
import ChatThread from "../common/ChatThread";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import { useToast, Modal, Button } from "../../ui";
import L from "../../../constants/baysportLabels";

export default function AdminPatientProfile({ staffMode = false }) {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientsBase = usePatientsBase();
  const toast = useToast();
  const isApp = useAdminHubUi();

  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);
  const subdomain = useSelector((state) => state.auth.tenant?.subdomain);
  const [competitionPickerOpen, setCompetitionPickerOpen] = useState(false);
  const [competitionEvents, setCompetitionEvents] = useState([]);
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const loadProfile = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    clinicAPI
      .getSportClientProfile(patientId)
      .then((r) => {
        setProfile(r);
        setEdit(patientToEdit(r.client));
      })
      .catch(() => {
        clinicAPI
          .getPatient(patientId)
          .then((r) => {
            setProfile({
              client: r.patient,
              groups: [],
              memberships: [],
              payments: [],
              documents: [],
            });
            setEdit(patientToEdit(r.patient));
          })
          .catch((e) => {
            toast.error(e.response?.data?.error || "Клиент не найден");
            navigate(patientsBase);
          });
      })
      .finally(() => setLoading(false));
  }, [patientId, navigate, toast]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const savePatient = async (e) => {
    e?.preventDefault?.();
    try {
      await clinicAPI.updatePatient(patientId, edit);
      toast.success("Сохранено");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const savePortalEmail = async (e) => {
    e?.preventDefault?.();
    setSavingEmail(true);
    try {
      await clinicAPI.updatePatient(patientId, { email: edit.email });
      toast.success("Email сохранён");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingEmail(false);
    }
  };

  const setPortalPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    setSettingPassword(true);
    try {
      await authAPI.adminSetUserPassword(patientId, newPassword);
      setNewPassword("");
      toast.success("Пароль задан");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSettingPassword(false);
    }
  };

  const archiveClient = async () => {
    if (!window.confirm("Отправить клиента в архив? История сохранится.")) return;
    try {
      await clinicAPI.updatePatient(patientId, {
        is_archived: true,
        ...edit,
      });
      toast.success("Клиент в архиве");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const writeMessage = () => {
    if (isApp) {
      navigate(`${patientsBase}/${patientId}/chat`, {
        state: { clientName: patient?.full_name || "" },
      });
      return;
    }
    setChatOpen(true);
  };

  const openCompetitionPicker = async () => {
    setCompetitionPickerOpen(true);
    setCompetitionLoading(true);
    try {
      const from = new Date().toISOString().slice(0, 10);
      const toDate = new Date();
      toDate.setMonth(toDate.getMonth() + 6);
      const to = toDate.toISOString().slice(0, 10);
      const data = await clinicAPI.getTrainingSessions({ from, to });
      const list = (data.sessions || []).filter(
        (s) => s.session_kind === "event" && s.event_kind === "competition",
      );
      setCompetitionEvents(list);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки событий");
      setCompetitionEvents([]);
    } finally {
      setCompetitionLoading(false);
    }
  };

  const addToCompetition = async (sessionId) => {
    try {
      await clinicAPI.addSessionRegistration(sessionId, {
        patient_id: patientId,
        status: "approved",
      });
      toast.success("Клиент добавлен в список");
      setCompetitionPickerOpen(false);
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const moderateRegistration = async (status) => {
    const label = status === "approved" ? "подтвердить" : "отклонить";
    if (!window.confirm(`${label.charAt(0).toUpperCase()}${label.slice(1)} регистрацию?`)) {
      return;
    }
    try {
      await clinicAPI.moderateClientRegistration(patientId, status);
      toast.success(status === "approved" ? "Регистрация подтверждена" : "Заявка отклонена");
      loadProfile();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось обработать заявку");
    }
  };

  if (loading && !profile) {
    if (isApp) {
      return (
        <HubScreen
          title="Клиент"
          onBack={() => navigate(patientsBase)}
          backLabel="К клиентам"
        >
          <p className={aa.hubHint}>Загрузка…</p>
        </HubScreen>
      );
    }
    return (
      <div className={s.patientPage}>
        <p style={{ color: "var(--color-text-muted)" }}>Загрузка…</p>
      </div>
    );
  }

  const patient = profile?.client;
  const sport = patient?.sport;
  const pendingRegistration = sport?.registration_status === "pending";

  const moderationBanner = pendingRegistration ? (
    <div className={s.registrationModerationBanner}>
      <p>Заявка на регистрацию ожидает подтверждения</p>
      <div className={s.membershipPayFormBtns}>
        <Button type="button" onClick={() => moderateRegistration("approved")}>
          Подтвердить
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => moderateRegistration("rejected")}
        >
          Отклонить
        </Button>
      </div>
    </div>
  ) : null;

  const profileBody = (
    <>
      {moderationBanner}
      <ClientProfileSinglePage
      patientId={patientId}
      patient={patient}
      profile={profile}
      edit={edit}
      setEdit={setEdit}
      sport={sport}
      onSave={savePatient}
      onArchive={archiveClient}
      onReload={loadProfile}
      writeMessage={writeMessage}
      subdomain={subdomain}
      newPassword={newPassword}
      setNewPassword={setNewPassword}
      onSavePortalEmail={savePortalEmail}
      onSetPortalPassword={setPortalPassword}
      savingPortalEmail={savingEmail}
      settingPortalPassword={settingPassword}
      onAddToCompetition={openCompetitionPicker}
    />
    </>
  );

  const competitionPicker = (
    <Modal
      open={competitionPickerOpen}
      onClose={() => setCompetitionPickerOpen(false)}
      title="Добавить на соревнование"
    >
      {competitionLoading ? (
        <p className={s.clientTabMuted}>Загрузка…</p>
      ) : competitionEvents.length === 0 ? (
        <p className={s.clientTabMuted}>
          Нет предстоящих соревнований. Создайте событие в календаре.
        </p>
      ) : (
        <ul className={s.competitionRegList}>
          {competitionEvents.map((ev) => (
            <li key={ev.id} className={s.competitionRegItem}>
              <div>
                <strong>{ev.title || "Соревнование"}</strong>
                <span className={s.clientTabMuted}>
                  {ev.start_datetime
                    ? new Date(ev.start_datetime).toLocaleDateString("ru-RU")
                    : ""}
                </span>
              </div>
              <Button type="button" size="sm" onClick={() => addToCompetition(ev.id)}>
                Добавить
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(patientsBase);
  };

  const chatModal = !isApp ? (
    <Modal
      open={chatOpen}
      onClose={() => setChatOpen(false)}
      title={`Чат — ${patient?.full_name || "клиент"}`}
    >
      <ChatThread mode="staff" patientId={patientId} variant="sheet" />
    </Modal>
  ) : null;

  if (isApp) {
    return (
      <HubScreen
        title={patient?.full_name || "Клиент"}
        onBack={handleBack}
        backLabel="Назад"
        bodyClassName={aa.clientProfileHubBody}
      >
        {profileBody}
        {competitionPicker}
        {chatModal}
      </HubScreen>
    );
  }

  return (
    <div className={s.patientPage}>
      <nav className={s.patientBreadcrumbs} aria-label="Навигация">
        <Link to={patientsBase}>{L.clients}</Link>
        <span aria-hidden>/</span>
        <span>{patient?.full_name || "Клиент"}</span>
      </nav>
      {profileBody}
      {competitionPicker}
      {chatModal}
    </div>
  );
}
